import { Controller } from "@hotwired/stimulus";
import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Markdown } from 'tiptap-markdown'
import { put } from '@rails/request.js'

import debounce from "../../../javascript/utils/debounce";

export default class extends Controller {
  static targets = ["input"]
  static values = {
    updateUrl: String,
  }

  editor = undefined

  connect(){
    this.setupEditor()
  }

  disconnect(){
    if (this.editor){
      this.editor.destroy()
    }
  }

  setupEditor(){
    if (this.hasInputTarget){
      this.setupRichTextEditor()

      this.inputTarget.addEventListener('keydown', this.onKeyDown.bind(this))

      this.inputTarget.addEventListener('input', debounce(async () => {
        await this.saveContent()
      }, 300))

      this.inputTarget.addEventListener('focusout', async () => {
        await this.saveContent()
      })
    }
  }

  setupRichTextEditor(){
    this.editor = new Editor({
      content: undefined,
      extensions: [
        StarterKit,
        Markdown.configure({
          breaks: false,               // New lines (\n) in markdown input are converted to <br>
          bulletListMarker: '-',       // <li> prefix in markdown output
          html: false,                  // Allow HTML input/output
          linkify: true,              // Create links from "https://..." text
          tightListClass: 'tight',     // Add class to <ul> allowing you to remove <p> margins when tight
          tightLists: true,            // No <p> inside <li> in markdown output
        }),
        Link.configure({
          HTMLAttributes: {
            class: "cursor-pointer",
          },
        }),
      ],
      editable: false,
    })
  }

  insertTextAtCursor(text) {
    document.execCommand("insertText", false, text)
  }

  undoIndentation(e){
    const selection = window.getSelection()
    if (!selection.rangeCount) return

    const range = selection.getRangeAt(0)

    const node = range.startContainer
    const offset = range.startOffset

    if (node.nodeType === Node.TEXT_NODE && node.nodeValue.substring(offset - 2, offset) === "  ") {
      e.preventDefault()

      // Remove the last 2 spaces
      node.nodeValue = node.nodeValue.substring(0, offset - 2) + node.nodeValue.substring(offset)
      range.setStart(node, offset - 2)
      range.setEnd(node, offset - 2)
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }

  onKeyDown(e){
    if (e.key === 'Tab'){
      e.preventDefault()
      this.insertTextAtCursor('  ')
      return
    }

    if (e.key === 'Backspace'){
      this.undoIndentation(e)
    }
  }

  async saveContent(){
    if (this.updateUrlValue){
      await this.editor.commands.setContent(this.inputTarget.innerText)

      await put(this.updateUrlValue, {
        contentType: 'application/json',
        body: JSON.stringify({
          rich_text_page: {
            content_json: JSON.stringify(this.editor.getJSON()),
            content_markdown: this.editor.storage.markdown.getMarkdown(),
          },
        }),
      })
    }
  }
}
